import React, { useState, useCallback, useEffect, useRef } from 'react'
import { Box, Button, Dialog, IconButton, Skeleton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import PrintIcon from '@mui/icons-material/Print'
import { styled } from '@mui/material/styles'
import { colors } from 'constants/theme'
import type { ManualPage } from './manuals/manual.types'
import ManualApi from 'api/manual.api'
import logoHorizontal from 'assets/logo/dental/logoHorizontal.png'

const ManualTitle = styled('h2')(() => ({
  fontSize: 22,
  fontWeight: 700,
  color: colors.textPrimary,
  margin: '16px 0 4px 0',
}))

const LastUpdated = styled('span')(() => ({
  fontSize: 13,
  color: '#999',
  display: 'block',
  marginBottom: 20,
}))

const StepContainer = styled('div')(() => ({
  marginBottom: 24,
  padding: '0 16px',
}))

const StepTitle = styled('div')(() => ({
  background: colors.themeSecondColor10 ?? 'rgba(28, 185, 154, 0.1)',
  color: '#1a1a1a',
  padding: '10px 14px',
  borderRadius: 4,
  fontWeight: 600,
  fontSize: 15,
  marginBottom: 8,
}))

const StepDescription = styled('div')(() => ({
  fontSize: 14,
  lineHeight: 1.8,
  color: '#2d2d2d',
  '& ul, & ol': { margin: '6px 0 12px 0', paddingLeft: 24, '& li': { marginBottom: 4 } },
  '& p': { marginBottom: 8 },
  '& strong': { fontWeight: 600 },
}))

const ScreenshotImg = styled('img')(() => ({
  maxWidth: '100%',
  borderRadius: 6,
  border: '1px solid #e0e0e0',
  cursor: 'pointer',
  margin: '8px 0 4px 0',
  transition: 'box-shadow 0.2s, opacity 0.3s ease-in',
  animation: 'manual-fade-in 0.35s ease-out',
  '&:hover': {
    boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
  },
  '@keyframes manual-fade-in': {
    from: { opacity: 0, transform: 'translateY(4px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
}))

const ScreenshotSkeleton = styled('div')(() => ({
  width: '100%',
  aspectRatio: '16 / 10',
  borderRadius: 6,
  border: '1px solid #e8e8e8',
  margin: '8px 0 4px 0',
  background: 'linear-gradient(90deg, #f3f3f3 0%, #e8f7f4 50%, #f3f3f3 100%)',
  backgroundSize: '200% 100%',
  animation: 'manual-skeleton-shimmer 1.4s ease-in-out infinite',
  '@keyframes manual-skeleton-shimmer': {
    '0%': { backgroundPosition: '-200% 0' },
    '100%': { backgroundPosition: '200% 0' },
  },
}))

const NoteBox = styled('div')(() => ({
  background: '#FFF8E1',
  border: '1px solid #FFE082',
  borderRadius: 4,
  padding: '8px 12px',
  fontSize: 13,
  color: '#5D4037',
  margin: '8px 0',
}))

function formatDateTh(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' })
  } catch {
    return iso
  }
}

/** Hook: fetch an authenticated image and return an object URL */
function useAuthImage(src: string | undefined): string | null {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!src) { setObjectUrl(null); return }

    let cancelled = false
    let localUrl: string | null = null

    ManualApi.getScreenshotBlob(src).then((blobUrl) => {
      if (cancelled) {
        if (blobUrl.startsWith('blob:')) URL.revokeObjectURL(blobUrl)
        return
      }
      localUrl = blobUrl
      setObjectUrl(blobUrl)
    })

    return () => {
      cancelled = true
      if (localUrl && localUrl.startsWith('blob:')) URL.revokeObjectURL(localUrl)
    }
  }, [src])

  return objectUrl
}

/** Component to render an authenticated screenshot — fetches only when near viewport */
function AuthScreenshot({ src, alt, onClick }: { src: string; alt: string; onClick: () => void }) {
  const [inView, setInView] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (inView) return
    const el = wrapperRef.current
    if (!el) return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true)
          io.disconnect()
        }
      },
      { rootMargin: '400px 0px' }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [inView])

  const blobUrl = useAuthImage(inView ? src : undefined)

  if (!blobUrl) {
    return (
      <div ref={wrapperRef}>
        <ScreenshotSkeleton />
      </div>
    )
  }

  return <ScreenshotImg src={blobUrl} alt={alt} onClick={onClick} loading="lazy" decoding="async" />
}

/** Build a complete standalone HTML document for printing */
async function buildPrintHtml(manual: ManualPage, logoSrc: string): Promise<string> {
  // Convert all screenshots to base64 for the print window (cross-origin safe)
  const screenshotMap = new Map<string, string>()
  const screenshotPromises = manual.steps
    .filter((step) => step.screenshot)
    .map(async (step) => {
      const base64 = await ManualApi.getScreenshotBase64(step.screenshot!)
      screenshotMap.set(step.screenshot!, base64)
    })
  await Promise.all(screenshotPromises)

  const stepsHtml = manual.steps.map((step) => {
    const imgSrc = step.screenshot ? (screenshotMap.get(step.screenshot) || step.screenshot) : ''
    return `
    <div class="step">
      <div class="step-title">ขั้นตอนที่ ${step.order}: ${step.title}</div>
      <div class="step-desc">${step.description}</div>
      ${step.screenshot ? `<div class="screenshot-wrap"><img class="screenshot" src="${imgSrc}" alt="${step.title}" /></div>` : ''}
      ${step.note ? `<div class="note">${step.note}</div>` : ''}
    </div>
  `}).join('')

  return `<!DOCTYPE html>
<html lang="th">
<head>
<meta charset="UTF-8">
<title>${manual.title} — Cliniter Dental</title>
<style>
  @page {
    size: A4 portrait;
    margin: 28mm 15mm 25mm 15mm;
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Sarabun', 'Noto Sans Thai', -apple-system, sans-serif;
    font-size: 11pt;
    line-height: 1.65;
    color: #1a1a1a;
    background: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* ---- Header (repeats every page) ---- */
  .print-header {
    position: fixed;
    top: 0; left: 0; right: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 0 6px 0;
    border-bottom: 2.5px solid #1CB99A;
    background: #fff;
    z-index: 100;
  }
  .print-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .print-header-left img {
    height: 30px;
    width: auto;
  }
  .print-header-left span {
    font-size: 13pt;
    font-weight: 700;
    color: #1CB99A;
  }
  .print-header-right {
    font-size: 10pt;
    font-weight: 600;
    color: #444;
  }

  /* ---- Footer (repeats every page) ---- */
  .print-footer {
    position: fixed;
    bottom: 0; left: 0; right: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0 0 0;
    border-top: 1px solid #ccc;
    font-size: 8pt;
    color: #888;
    background: #fff;
    z-index: 100;
  }

  /* ---- Body ---- */
  .print-body {
    margin-top: 50px;
    margin-bottom: 35px;
  }
  .manual-title {
    font-size: 18pt;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 2px;
  }
  .manual-date {
    font-size: 9pt;
    color: #888;
    margin-bottom: 18px;
  }

  /* ---- Steps ---- */
  .step {
    page-break-inside: avoid;
    margin-bottom: 20px;
  }
  .step-title {
    background: rgba(28, 185, 154, 0.1);
    padding: 7px 12px;
    border-radius: 4px;
    border-left: 4px solid #1CB99A;
    font-weight: 600;
    font-size: 12pt;
    margin-bottom: 6px;
    color: #1a1a1a;
  }
  .step-desc {
    font-size: 10.5pt;
    line-height: 1.7;
    color: #2d2d2d;
  }
  .step-desc p { margin-bottom: 5px; }
  .step-desc ul, .step-desc ol { margin: 4px 0 8px 0; padding-left: 22px; }
  .step-desc li { margin-bottom: 2px; }
  .step-desc strong { font-weight: 600; }

  /* ---- Screenshot ---- */
  .screenshot-wrap {
    margin: 8px 0;
    text-align: center;
    page-break-inside: avoid;
  }
  .screenshot {
    max-width: 100%;
    max-height: 280px;
    border: 1px solid #ddd;
    border-radius: 4px;
    object-fit: contain;
  }

  /* ---- Note ---- */
  .note {
    background: #FFF8E1;
    border: 1px solid #FFE082;
    border-radius: 4px;
    padding: 6px 10px;
    font-size: 9.5pt;
    color: #5D4037;
    margin: 6px 0;
    page-break-inside: avoid;
  }
  .note::before {
    content: "หมายเหตุ: ";
    font-weight: 600;
  }
</style>
</head>
<body>
  <div class="print-header">
    <div class="print-header-left">
      <img src="${logoSrc}" alt="Cliniter" />
      <span>Cliniter Dental</span>
    </div>
    <div class="print-header-right">${manual.title}</div>
  </div>

  <div class="print-footer">
    <span>เอกสารนี้เป็นคู่มือการใช้งานระบบ Cliniter Dental</span>
    <span>อัปเดตล่าสุด: ${formatDateTh(manual.lastUpdated)}</span>
  </div>

  <div class="print-body">
    <div class="manual-title">${manual.title}</div>
    <div class="manual-date">อัปเดตล่าสุด: ${formatDateTh(manual.lastUpdated)}</div>
    ${stepsHtml}
  </div>

  <script>
    window.onload = function() { window.print(); window.close(); };
  </script>
</body>
</html>`
}

interface ManualContentProps {
  manual: ManualPage
}

export default function ManualContent({ manual }: ManualContentProps) {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null)
  const lightboxBlobUrl = useAuthImage(lightboxSrc || undefined)

  const handleExportPdf = useCallback(async () => {
    const printWindow = window.open('', '_blank', 'width=900,height=700')
    if (!printWindow) return

    // Show loading message while converting images
    printWindow.document.write('<html><body><p style="font-family:sans-serif;padding:32px">กำลังเตรียมเอกสาร...</p></body></html>')

    const logoSrc = window.location.origin + (logoHorizontal.startsWith('/') ? '' : '/') + logoHorizontal
    const html = await buildPrintHtml(manual, logoSrc)
    printWindow.document.open()
    printWindow.document.write(html)
    printWindow.document.close()
  }, [manual])

  return (
    <Box sx={{ padding: '0 16px', height: '100%', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <ManualTitle>{manual.title}</ManualTitle>
          <LastUpdated>อัปเดตล่าสุด: {formatDateTh(manual.lastUpdated)}</LastUpdated>
        </div>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handleExportPdf}
          sx={{
            mt: 2,
            borderColor: colors.themeSecondColor,
            color: colors.themeSecondColor,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            '&:hover': {
              borderColor: colors.themeSecondColor,
              backgroundColor: 'rgba(28, 185, 154, 0.06)',
            },
          }}
        >
          Export PDF
        </Button>
      </Box>

      {manual.steps.map((step) => (
        <StepContainer key={step.order}>
          <StepTitle>ขั้นตอนที่ {step.order}: {step.title}</StepTitle>
          <StepDescription dangerouslySetInnerHTML={{ __html: step.description }} />
          {step.screenshot && (
            <AuthScreenshot
              src={step.screenshot}
              alt={step.title}
              onClick={() => setLightboxSrc(step.screenshot!)}
            />
          )}
          {step.note && <NoteBox dangerouslySetInnerHTML={{ __html: step.note }} />}
        </StepContainer>
      ))}

      <Dialog
        open={!!lightboxSrc}
        onClose={() => setLightboxSrc(null)}
        maxWidth={false}
        PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', maxWidth: '90vw', maxHeight: '90vh' } }}
      >
        <IconButton
          onClick={() => setLightboxSrc(null)}
          sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
        >
          <CloseIcon />
        </IconButton>
        {lightboxBlobUrl ? (
          <img
            src={lightboxBlobUrl}
            alt="enlarged"
            style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 4 }}
          />
        ) : lightboxSrc ? (
          <ScreenshotSkeleton style={{ width: 600, maxWidth: '90vw', aspectRatio: '16 / 10' }} />
        ) : null}
      </Dialog>
    </Box>
  )
}
